import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { parseAdminHomeLayout, type AdminHomeLayout } from "./adminHomeLayout";

import { USER_PREFS_QUERY_KEY } from "@/features/preferences/themePreferences";
import { request } from "@/lib/api";

type PrefsResponse = { adminHome?: unknown };

/**
 * The signed-in user's saved Admin Home layout, or the default when nothing is
 * saved, the request fails, or the stored value is invalid. Shares the
 * preferences query (and cache) with the theme and sidebar controls.
 */
export function useAdminHomeLayout(): AdminHomeLayout {
  const { data } = useQuery({
    queryKey: USER_PREFS_QUERY_KEY,
    queryFn: () => request.get<PrefsResponse>("/users/me/preferences"),
    staleTime: 60_000,
  });
  const raw = (data as PrefsResponse | undefined)?.adminHome;
  return useMemo(() => parseAdminHomeLayout(raw), [raw]);
}

/** Persist a layout, or `null` to reset to the default. */
export function saveAdminHomeLayout(
  layout: AdminHomeLayout | null,
): Promise<PrefsResponse> {
  return request.patch<PrefsResponse>("/users/me/preferences", {
    adminHome: layout,
  });
}
